import classNames from "classnames";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { trpc } from "../../../utils/trpc";
import { RemovingThrobber } from "../../removing_throbber";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { BoxItem } from "../box_item";
import { Disabler } from "../disabler";
import { useIsAdmin } from "../hooks/use_is_admin";
import { useServerId } from "../hooks/use_server_id";
import SimpleMemberDisplayName from "../member_display_name/simple_member_display_name";
import { SelectableBox } from "../selectable_box";
import { WithTooltipPortaled } from "../tooltips/with_tooltip_portaled";
import { TemplateSelection } from "./event_template_page";

export function EventTemplateModalRow(props: {
	item_id: string;
	item_name: string;
	author_id: string;
	bookmark_id?: string;
	created_at: DateTime;
	onInfoClick: React.MouseEventHandler<HTMLButtonElement>;
	onUseClick: () => void;
	onFavoriteClicked?: () => void;
	onRowClick: () => void;
	selected_template: TemplateSelection;
	info_text?: ReactNode;
	is_event_list: boolean;
	are_bookmarks_full?: boolean;
}) {
	const is_bookmarked = !!props.bookmark_id;

	const author_verb = props.is_event_list ? "Created" : "Favorited";

	const server_id = useServerId();
	const context = trpc.useContext();
	const delete_bookmark_mutation =
		trpc.event_template.deleteBookmark.useMutation({
			onSuccess: (data) => {
				context.event_template.getBookmarkedList.setData(
					(old_data) => {
						return old_data.filter(
							(val) => val.id !== props.bookmark_id
						);
					},
					{ guild_id: server_id }
				);
			},
		});

	const isAdmin = useIsAdmin();

	const is_selected =
		props.selected_template?.message_id === props.item_id ||
		props.selected_template?.template_id === props.item_id;

	const should_force_show_buttons =
		is_selected || delete_bookmark_mutation.isLoading;

	const mobile_responsive_class_names = classNames({
		block: is_selected,
		"hidden group-hover:block sm:block": !is_selected,
	});

	return (
		<Disabler disabled={delete_bookmark_mutation.isLoading}>
			<BoxItem
				bgColor="dark"
				className="group cursor-pointer"
				onClick={props.onRowClick}
			>
				<div className="flex w-full items-center gap-4">
					<div className="flex w-11/12 flex-col text-content-primary-darker">
						<div className="flex w-full flex-col items-center gap-2 sm:flex-row">
							<div
								className="overflow-hidden overflow-ellipsis whitespace-nowrap text-lg font-bold text-content-primary"
								title={props.item_name}
							>
								{props.item_name}
							</div>
							<div className="text-xs sm:ml-auto">
								{props.info_text}
							</div>
						</div>
						<div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-center">
							<div>
								<WithTooltipPortaled
									tooltip={props.created_at.toLocaleString({
										dateStyle: "full",
										timeStyle: "medium",
									})}
									style={"Info"}
								>
									{author_verb} by{" "}
									<SimpleMemberDisplayName
										user_id={props.author_id}
									/>{" "}
									{props.created_at.toRelative()}
								</WithTooltipPortaled>
							</div>
							<div
								className={classNames(
									mobile_responsive_class_names,
									"ml-auto"
								)}
							>
								<div
									className={classNames(
										"ml-auto flex items-center gap-4 text-sm transition-all group-hover:opacity-100",
										{
											"opacity-100":
												should_force_show_buttons,
											"opacity-0":
												!should_force_show_buttons,
										}
									)}
								>
									<div>
										<ModifySettingsButton
											className={classNames(
												"ml-auto w-fit",
												{
													"text-warning hover:text-warning-hover":
														!!props.bookmark_id,
												}
											)}
											disabled={
												!isAdmin?.data ||
												(!is_bookmarked &&
													props.are_bookmarks_full)
											}
											onClick={() => {
												props.bookmark_id
													? delete_bookmark_mutation.mutate(
															{
																template_id:
																	props.bookmark_id,
															}
													  )
													: props.onFavoriteClicked();
											}}
											hide_arrow
											title={
												props.are_bookmarks_full
													? "Favorites full"
													: undefined
											}
										>
											<div
												className={
													"flex items-center gap-2"
												}
											>
												<div
													key={
														delete_bookmark_mutation.isLoading
															? "removing"
															: "not_removing"
													}
												>
													{delete_bookmark_mutation.isLoading ? (
														<RemovingThrobber />
													) : props.bookmark_id ? (
														<div>
															<i className="fas fa-trash" />
														</div>
													) : (
														<div>
															<i className="fas fa-star"></i>
														</div>
													)}
												</div>
												{props.bookmark_id
													? props.is_event_list
														? "Unfavorite"
														: "Delete"
													: "Favorite"}
											</div>
										</ModifySettingsButton>
									</div>
									<div>
										<ModifySettingsButton
											className="ml-auto w-fit"
											onClick={props.onUseClick}
											hide_arrow
										>
											<i className="fas fa-check"></i> Use
										</ModifySettingsButton>
									</div>
								</div>
							</div>
						</div>
					</div>
					<span
						className={classNames(
							mobile_responsive_class_names,
							"ml-auto"
						)}
					>
						<div
							className={classNames(
								"self-center text-content-primary transition-all hover:!opacity-100 group-hover:opacity-50",
								{
									"opacity-100": should_force_show_buttons,
									"opacity-0": !should_force_show_buttons,
								}
							)}
						>
							<button onClick={props.onInfoClick}>
								<i className="fas fa-info-circle"></i>
							</button>
						</div>
					</span>
				</div>
			</BoxItem>
		</Disabler>
	);
}
function HoverableStarEmpty(props: { disable_hover?: boolean }) {
	return (
		<WithTooltipPortaled
			disable_click={!props.disable_hover}
			tooltip={`Add to server favorites${
				props.disable_hover ? " (requires Manage Sesh permissions)" : ""
			}`}
			style="Info"
		>
			<div
				className={classNames("fa-stack w-8", {
					"group/star": !props.disable_hover,
				})}
			>
				<i className="far fa-star fa-stack-1x opacity-100 transition-all group-hover/star:opacity-0"></i>
				<i className="fas fa-star fa-stack-1x opacity-0 transition-opacity group-hover/star:opacity-100"></i>
			</div>
		</WithTooltipPortaled>
	);
}
function HoverableStarFull(props: { disable_hover?: boolean }) {
	return (
		<WithTooltipPortaled
			disable_click={!props.disable_hover}
			tooltip={`Unfavorite from server${
				props.disable_hover ? " (requires Manage Sesh permissions)" : ""
			}`}
			style="Info"
		>
			<div
				className={classNames("fa-stack w-8", {
					"group/star": !props.disable_hover,
				})}
			>
				<i className="fas fa-star fa-stack-1x opacity-100 transition-all group-hover/star:opacity-0"></i>
				<i className="far fa-star fa-stack-1x opacity-0 transition-opacity group-hover/star:opacity-100"></i>
			</div>
		</WithTooltipPortaled>
	);
}
